import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Loader } from '@common/components';
import { STATUSES, LABEL_KEYS } from '@dashboard/components/SyncMonitor/statuses';
import classes from './StatsSummaryPanel.module.scss';

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function formatScope(year, month, view) {
  if (view === 'month' && month) {
    return `${MONTH_NAMES[month - 1]} ${year}`;
  }
  return String(year);
}

export default function StatsSummaryPanel({
  summary,
  isFetching,
  activeStatus,
  onSelectStatus,
  year,
  month,
  view,
}) {
  const { t } = useTranslation('dashboard\\SyncMonitorView', {
    useSuspense: false,
  });

  if (isFetching && !summary) {
    return (
      <div className={classes.loaderWrap}>
        <Loader />
      </div>
    );
  }

  const total = summary?.total ?? 0;
  const scope = formatScope(year, month, view);

  return (
    <div className={classes.panel}>
      <div className={classes.header}>
        <h3 className={classes.title}>
          {t('summaryTitle')}
          <span className={classes.scope}>{t('summaryScope', { scope })}</span>
        </h3>
        <div className={classes.totalBlock}>
          <span className={classes.totalNumber}>
            {new Intl.NumberFormat('en-US').format(total)}
          </span>
          <span className={classes.totalLabel}>{t('summaryTotalLabel')}</span>
        </div>
      </div>

      <div className={classes.grid}>
        {STATUSES.map((status) => {
          const count = summary?.[status] ?? 0;
          const pct = total > 0 ? (count / total) * 100 : 0;
          const isActive = activeStatus === status;
          return (
            <button
              type="button"
              key={status}
              className={`${classes.tile} ${classes[status]} ${isActive ? classes.active : ''}`}
              onClick={() => onSelectStatus(isActive ? null : status)}
              disabled={count === 0}
              title={t('summaryTileHint', { status: t(LABEL_KEYS[status]) })}
            >
              <span className={classes.tileTopRow}>
                <span className={classes.tileCount}>{count}</span>
                <span className={classes.tilePct}>{pct.toFixed(1)}%</span>
              </span>
              <span className={classes.tileBar}>
                <span
                  className={`${classes.tileBarFill} ${classes[`barFill_${status}`]}`}
                  style={{ width: `${Math.max(pct, count > 0 ? 2 : 0)}%` }}
                />
              </span>
              <span className={classes.tileLabel}>{t(LABEL_KEYS[status])}</span>
              <span className={classes.tileScope}>{t('summaryTileScope', { scope })}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

StatsSummaryPanel.propTypes = {
  summary: PropTypes.object,
  isFetching: PropTypes.bool,
  activeStatus: PropTypes.string,
  onSelectStatus: PropTypes.func.isRequired,
  year: PropTypes.number.isRequired,
  month: PropTypes.number.isRequired,
  view: PropTypes.oneOf(['year', 'month']).isRequired,
};

StatsSummaryPanel.defaultProps = {
  summary: null,
  isFetching: false,
  activeStatus: null,
};
